import Button from "@/components/ui/Button";
import TextField from "@/components/ui/Textfield";
import React, { useEffect, useState } from "react";
import useGlobalStore from "@/store/globalStore";
import { useSearchParams } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import useMediaUserOnboardingStore from "../store/mediaUserOnboardingStore";

interface Props {
  onNext: () => void;
}

const StepOneMediaUserOnboarding: React.FC<Props> = ({ onNext }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { sendUserData, inviteData } = useMediaUserOnboardingStore(
    useShallow((state) => ({
      sendUserData: state.sendUserData,
      inviteData: state.inviteData,
    }))
  );
  const [searchParams] = useSearchParams();

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    title: "",
    password: "",
    password_confirmation: "",
  });

  // Pre-fill form when inviteData becomes available
  useEffect(() => {
    if (inviteData) {
      setFormData((prev) => ({
        ...prev,
        first_name: inviteData.first_name || prev.first_name,
        last_name: inviteData.last_name || prev.last_name,
        email: inviteData.email || prev.email,
        title: inviteData.role_type || prev.title,
      }));
    }
  }, [inviteData]);

  const setError = useGlobalStore((state) => state.setError);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const {
      first_name,
      last_name,
      email,
      title,
      password,
      password_confirmation,
    } = formData;

    if (!first_name.trim()) return setError("First Name is required.");
    if (!last_name.trim()) return setError("Last Name is required.");
    if (!email.trim()) return setError("Email is required.");
    if (!title.trim()) return setError("Title is required.");
    if (!password.trim()) return setError("Password is required.");
    if (!password_confirmation.trim())
      return setError("Confirm password is required.");

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return setError("Please enter a valid email address.");
    }

    if (password !== password_confirmation) {
      return setError("Passwords do not match.");
    }

    setIsLoading(true);

    try {
      await sendUserData({
        first_name,
        last_name,
        email,
        title,
        password,
        password_confirmation,
        token: searchParams.get("token"),
      });

      onNext();
      setIsLoading(false);
    } catch (err: any) {
      setIsLoading(false);
      setError(err?.message || "Failed to submit user data.");
    }
  };

  return (
    <div className="bg-[#F8F8F8] flex flex-col items-center justify-center px-4 py-14">
      <div className="max-w-md w-full text-center mb-6">
        <h1 className="text-5xl font-bold text-[#EA33C0] mb-4">MediaBridge</h1>
        <h2 className="text-xl font-semibold mb-2">Welcome, there!</h2>
        <p className="text-sm text-gray-600">
          You've been invited to join your team. <br />
          Please fill in the required fields so we can finish setting up your
          account.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="w-full max-w-xl space-y-4">
        <div className="flex items-center">
          <div className="mr-2 w-56 font-semibold">First Name</div>
          <TextField
            type="text"
            name="first_name"
            value={formData.first_name}
            onChange={onChange}
            placeholder="First Name"
            className="w-full"
          />
        </div>

        <div className="flex items-center">
          <div className="mr-2 w-56 font-semibold">Last Name</div>
          <TextField
            type="text"
            name="last_name"
            value={formData.last_name}
            onChange={onChange}
            placeholder="Last Name"
            className="w-full"
          />
        </div>

        <div className="flex items-center">
          <div className="mr-2 w-56 font-semibold">Email</div>
          <TextField
            type="email"
            name="email"
            value={formData.email}
            onChange={onChange}
            placeholder="example@email.com"
            className="w-full"
          />
        </div>

        <div className="flex items-center">
          <div className="mr-2 w-56 font-semibold">Title</div>
          <TextField
            type="text"
            name="title"
            value={formData.title}
            onChange={onChange}
            placeholder="Title"
            className="w-full"
          />
        </div>

        <div className="flex items-center">
          <div className="mr-2 w-56 font-semibold">Password</div>
          <TextField
            type="password"
            name="password"
            value={formData.password}
            onChange={onChange}
            placeholder="Password"
            className="w-full"
          />
        </div>

        <div className="flex items-center">
          <div className="mr-2 w-56 font-semibold">Confirm Password</div>
          <TextField
            type="password"
            name="password_confirmation"
            value={formData.password_confirmation}
            onChange={onChange}
            placeholder="Confirm Password"
            className="w-full"
          />
        </div>

        <div className="pt-4 text-center flex justify-center">
          <Button type="primary" onClick={handleSubmit} isLoading={isLoading}>
            Save Info
          </Button>
        </div>
      </form>
    </div>
  );
};

export default StepOneMediaUserOnboarding;
